import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";

/**
 * First-login legal disclaimer dialog.
 *
 * Flow
 * ----
 * 1. After a successful login (or on every app boot for existing tokens)
 *    the app calls `GET /api/disclaimer/status`.
 * 2. If `needs_acceptance` is true, this dialog opens with the 7 required
 *    checkboxes and links to /terms and /privacy.
 * 3. User must tick every box; the "Accept" button is disabled until then.
 * 4. On Accept we POST `/api/disclaimer/accept` with the version the user
 *    saw plus the list of ticked IDs. On success we call `onAccepted()`
 *    so the parent can close the dialog and refresh user state.
 *
 * The dialog is deliberately non-dismissible (no close button, no Esc)
 * when the backend flagged `needs_acceptance`. We achieve this via
 * onOpenChange={() => {}} on the shadcn Dialog.
 */
export const DisclaimerDialog = ({ open, onAccepted, onError }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [payload, setPayload] = useState(null); // { current_version, required_checkboxes }
  const [checkedMap, setCheckedMap] = useState({});

  // Load the current version + checkbox copy every time the dialog opens
  // so a legal bump mid-session shows the fresh text (stale sessions get
  // caught by the backend's version mismatch check too, as a safety net).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    apiGet("/disclaimer/status")
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
        // Reset ticks on every open.
        const seed = {};
        for (const cb of data?.required_checkboxes || []) seed[cb.id] = false;
        setCheckedMap(seed);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error("Could not load disclaimer. Please try again.");
        onError?.(e);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, onError]);

  const allTicked = useMemo(() => {
    const list = payload?.required_checkboxes || [];
    if (!list.length) return false;
    return list.every((cb) => !!checkedMap[cb.id]);
  }, [payload, checkedMap]);

  const handleAccept = async () => {
    if (!payload || !allTicked) return;
    setSubmitting(true);
    try {
      await apiPost("/disclaimer/accept", {
        version: payload.current_version,
        accepted_ids: Object.keys(checkedMap).filter((k) => checkedMap[k]),
      });
      toast.success("Thanks. Disclaimer recorded.");
      onAccepted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Please try again.";
      toast.error("Could not save acceptance", { description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* non-dismissible */ }}>
      <DialogContent
        className="max-w-2xl"
        data-testid="disclaimer-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-amber-500/15 p-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Before you continue</DialogTitle>
              <DialogDescription>
                Please read and acknowledge each statement below. This appears once per
                disclaimer version.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <ScrollArea className="max-h-[55vh] pr-3 -mr-3">
              <div className="space-y-3">
                {(payload?.required_checkboxes || []).map((cb) => {
                  const isChecked = !!checkedMap[cb.id];
                  return (
                    <label
                      key={cb.id}
                      htmlFor={`disclaimer-cb-${cb.id}`}
                      className={`flex gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                        isChecked ? "bg-emerald-500/5 border-emerald-500/40" : "hover:bg-muted/50"
                      }`}
                      data-testid={`disclaimer-cb-row-${cb.id}`}
                    >
                      <Checkbox
                        id={`disclaimer-cb-${cb.id}`}
                        checked={isChecked}
                        onCheckedChange={(v) =>
                          setCheckedMap((prev) => ({ ...prev, [cb.id]: !!v }))
                        }
                        className="mt-0.5 shrink-0"
                        data-testid={`disclaimer-cb-${cb.id}`}
                      />
                      <span className="text-sm leading-relaxed text-foreground">{cb.label}</span>
                    </label>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="text-xs text-muted-foreground border-t pt-3">
              By accepting you confirm you have read our{" "}
              <Link to="/terms" target="_blank" rel="noreferrer" className="underline hover:text-foreground" data-testid="disclaimer-link-terms">
                Terms &amp; Conditions
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-foreground" data-testid="disclaimer-link-privacy">
                Privacy Policy
              </Link>
              . Version {payload?.current_version || "1.0"}.
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                onClick={handleAccept}
                disabled={!allTicked || submitting}
                data-testid="disclaimer-accept-btn"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <><ShieldCheck className="h-4 w-4 mr-2" /> I understand &amp; accept</>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DisclaimerDialog;
